import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';

const DEFAULT_BUTTON_CONTENT = (
  <FontAwesomeIcon
    icon={faEllipsisVertical as IconProp}
    className='h-5 w-5 hover:text-brand'
  />
);

export type FloatingMenuItem = {
  /** Unique key for the item - uses label if not provided */
  key?: string;
  /** Icon to display before the label */
  icon?: React.ReactNode;
  /** Text label for the menu item */
  label: string;
  /** Click handler for the menu item */
  onClick: () => void;
  /** Whether the item is disabled */
  disabled?: boolean;
};

type FloatingMenuProps = {
  /** Array of menu items to display */
  items: FloatingMenuItem[];
  /** Button class - can be a string or function receiving isOpen state */
  buttonClassName?: string | ((isOpen: boolean) => string);
  buttonText?: React.ReactNode;
  placement?: Placement;
  /** Width of the menu panel */
  width?: string;
  /** Whether to use a portal. Set to false when inside another portal (e.g. HeadlessUI Listbox) */
  usePortal?: boolean;
};

export function FloatingMenu({
  items,
  buttonClassName,
  buttonText = DEFAULT_BUTTON_CONTENT,
  placement = 'bottom-end',
  width = 'w-44',
  usePortal = true,
}: FloatingMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Destructure x, y, strategy separately (not floatingStyles) for framer-motion compatibility
  const { refs, x, y, strategy, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    // Portal renders to body (needs fixed positioning relative to viewport)
    // Non-portal renders in place (needs absolute positioning relative to offsetParent)
    strategy: usePortal ? 'fixed' : 'absolute',
    middleware: [
      offset(4),
      flip({
        fallbackAxisSideDirection: 'start',
        fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
      }),
      shift({ padding: 4 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const close = () => setIsOpen(false);

  const menuContent = (
    <motion.div
      ref={refs.setFloating}
      role='menu'
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
      }}
      className={classNames(
        'z-[10001] rounded-md border border-outline-variant bg-white shadow-lg focus:outline-none',
        width
      )}
      initial={{
        opacity: 0,
        scale: 0.8,
        transformOrigin: 'top right',
      }}
      animate={{
        opacity: 1,
        scale: 1,
        transformOrigin: 'top right',
      }}
      exit={{
        opacity: 0,
        scale: 0.8,
        transformOrigin: 'top right',
      }}
      transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
      {...getFloatingProps()}
    >
      {items.map((item) => (
        <button
          key={item.key ?? item.label}
          role='menuitem'
          type='button'
          disabled={item.disabled}
          className={classNames(
            'block w-full cursor-pointer border-b border-outline-variant px-4 py-5 text-left text-sm font-medium text-copy-body last:border-b-0 hover:text-brand',
            item.disabled && 'cursor-not-allowed opacity-50'
          )}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!item.disabled) {
              close();
              item.onClick();
            }
          }}
        >
          <div className='flex items-center'>
            {item.icon && (
              <span className='mr-3 h-5 w-5 flex-shrink-0'>{item.icon}</span>
            )}
            <span>{item.label}</span>
          </div>
        </button>
      ))}
    </motion.div>
  );

  return (
    <div className='relative flex text-left'>
      <button
        ref={refs.setReference}
        type='button'
        className={
          buttonClassName
            ? typeof buttonClassName === 'function'
              ? buttonClassName(isOpen)
              : buttonClassName
            : classNames(
                'duration-250 flex h-10 w-10 items-center justify-center rounded-full text-secondary transition-all hover:bg-shade-8 hover:text-primary active:bg-shade-12',
                {
                  'bg-gray-100 text-primary': isOpen,
                }
              )
        }
        {...getReferenceProps({
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
          },
          onPointerDown: (e) => {
            e.stopPropagation();
            e.preventDefault();
          },
        })}
      >
        {buttonText}
      </button>

      <AnimatePresence>
        {isOpen &&
          (usePortal ? (
            <FloatingPortal>{menuContent}</FloatingPortal>
          ) : (
            menuContent
          ))}
      </AnimatePresence>
    </div>
  );
}
